/**
 * Types for the platform-api credit-profile endpoints
 * (epic #1033 E4-C1, #1125).
 *
 * Credit lives on the customer's owner account (orgs ↔ accounts are 1:1
 * via `orgs.owner_account_id`). The spendable line is a `credit_grants`
 * row (#1998). Payment terms live on `accounts.billing_grace_days`:
 * "账期 N days" means the statement is due N calendar days after period
 * end, and that is the column statement generation reads for `due_date`
 * (router#1999). `accounts.billing_cycle_days` is leftover under monthly
 * anchor billing (`statement_anchor_day_utc8` owns the period) — it is
 * no longer written. NULL grace falls back to
 * `credit_settings.default_grace_days`.
 *
 * Ledger money is INTEGER micro-USD (never floats). Operator write input
 * is INTEGER CNY cents (router#2036): submit carries `*_cny_cents`, and
 * approval locks USD micros at the policy USD_CNY rate. Requested payment
 * terms are 1–120 days with no tiers; the statement anchor is a 1–28
 * calendar day so it never lands past the shortest month.
 *
 * Application flow (router#1999): every submit lands `pending_approval`
 * and creates the dual-sign approval request. Submit never moves an
 * account column. The requester cannot be the decider.
 *
 * A contract attachment is MANDATORY: the submit references an existing
 * `finance_attachments` object (entity_type `credit_contract`) and is
 * rejected fail-closed before any DB write when the key is missing.
 *
 * Endpoints:
 *   GET  /internal/ops/credit-settings
 *   PUT  /internal/ops/credit-settings
 *   POST /internal/ops/credit-applications
 *   GET  /internal/ops/credit-applications?org_id=…
 */

/** Upper bound on operator CNY-cent inputs (JS-safe). */
export const CREDIT_APPLICATION_MAX_LIMIT_CNY_CENTS = 1_000_000_000_000_000;

/**
 * Long-term credit application reason (router#2077). Required on submit
 * after trim; historical rows stay NULL and read as "未填写". Temporary
 * credit does not carry this field — it already has expected arrival as
 * the factual basis.
 */
export const CREDIT_APPLICATION_REASON_MIN_LENGTH = 8;
export const CREDIT_APPLICATION_REASON_MAX_LENGTH = 500;

/** Trim + length-bound the long-term reason. Null when missing or invalid. */
export function normalizeCreditApplicationReason(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const reason = value.trim();
  if (
    reason.length < CREDIT_APPLICATION_REASON_MIN_LENGTH ||
    reason.length > CREDIT_APPLICATION_REASON_MAX_LENGTH
  ) {
    return null;
  }
  return reason;
}

/**
 * Retired operator-write field names. A body that still carries any of
 * these is refused — they must not be silently ignored and stored as CNY
 * (a 7× mis-fill). Read models may still expose `*_usd_micros`.
 */
export const CREDIT_OPERATOR_LEGACY_USD_INPUT_KEYS = [
  'requested_limit_usd_micros',
  'expected_arrival_amount_usd_micros',
  'limit_usd_micros',
] as const;

export function creditOperatorLegacyUsdInputKeys(payload: object): string[] {
  const found: string[] = [];
  for (const key of CREDIT_OPERATOR_LEGACY_USD_INPUT_KEYS) {
    if (Object.prototype.hasOwnProperty.call(payload, key)) {
      found.push(key);
    }
  }
  return found;
}

// ─── Platform defaults singleton ─────────────────────────────────────────────

/** Lowest / highest legal statement anchor day (UTC+8 calendar day). */
export const CREDIT_STATEMENT_ANCHOR_DAY_MIN = 1;
export const CREDIT_STATEMENT_ANCHOR_DAY_MAX = 28;

export interface CreditSettings {
  /** Default per-account credit limit in integer micro-USD (>= 0). */
  default_limit_usd_micros: number;
  /** Default billing cycle length in days (> 0). */
  default_cycle_days: number;
  /** Default grace days after cycle close before overdue (>= 0). */
  default_grace_days: number;
  /** Monthly statement anchor day, UTC+8 (1–28). */
  statement_anchor_day_utc8: number;
  updated_at: string | null;
  updated_by_ops_user_id: string | null;
}

export interface GetCreditSettingsResponse {
  settings: CreditSettings;
}

/** Partial update: at least one field must be present. */
export interface UpdateCreditSettingsRequest {
  default_limit_usd_micros?: number;
  default_cycle_days?: number;
  default_grace_days?: number;
  statement_anchor_day_utc8?: number;
}

export interface UpdateCreditSettingsResponse {
  settings: CreditSettings;
}

// ─── Application schema ──────────────────────────────────────────────────────

export const CREDIT_APPLICATION_STATUSES = [
  'pending_approval',
  'approved',
  'rejected',
] as const;
export type CreditApplicationStatus = (typeof CREDIT_APPLICATION_STATUSES)[number];

export function isCreditApplicationStatus(value: unknown): value is CreditApplicationStatus {
  return (
    typeof value === 'string' &&
    (CREDIT_APPLICATION_STATUSES as readonly string[]).includes(value)
  );
}

export interface CreditApplication {
  id: string;
  account_id: string;
  requested_limit_cny_cents: number | null;
  requested_limit_usd_micros: number;
  /** Locked USD_CNY rate at approval; null while pending. */
  applied_rate: number | null;
  requested_cycle_days: number;
  /** `finance_attachments.object_key` of the mandatory contract (entity_type `credit_contract`). */
  contract_attachment_key: string;
  status: CreditApplicationStatus;
  requester_ops_user_id: string;
  /**
   * Requester justification. Null on rows filed before router#2077.
   * Readers must render that as "未填写", not as an error or a blank line.
   */
  reason: string | null;
  created_at: string;
  updated_at: string;
}

/** Per-account credit columns (NULL = inherit the platform default). */
export interface CreditProfile {
  /**
   * Active long-term grant amount, or NULL when none. Kept as the existing
   * ops-console field so the customer-360 credit block does not go blank
   * before E4. Not the spendability sum — temporary is split out below.
   */
  credit_limit_usd_micros: number | null;
  /** Active long-term grant CNY original, or NULL when none / not archived. */
  credit_limit_cny_cents?: number | null;
  /** Locked USD_CNY rate on the active long-term grant, or NULL. */
  credit_limit_applied_rate?: number | null;
  /** Active temporary grant amount, or NULL/absent when none. */
  temporary_limit_usd_micros?: number | null;
  /** Active temporary grant CNY original, or NULL when none / not archived. */
  temporary_limit_cny_cents?: number | null;
  /** Locked USD_CNY rate on the active temporary grant, or NULL. */
  temporary_limit_applied_rate?: number | null;
  /**
   * Leftover column. Monthly statements are cut on
   * `statement_anchor_day_utc8`; this field is not written on approve
   * (router#1999) and is not read for `due_date`.
   */
  billing_cycle_days: number | null;
  /**
   * Per-account payment terms in days after period end. NULL inherits
   * `credit_settings.default_grace_days`. This is the column statement
   * generation reads for `due_date`.
   */
  billing_grace_days: number | null;
}

/**
 * Effective values: the per-account override, else the platform default.
 *
 * `limit_usd_micros` is the active grant cap (long-term + temporary), or
 * NULL when the account has no grant — prepaid, cannot overdraw. It is
 * never coalesced onto `default_limit_usd_micros` (router#2061).
 * `cycle_days` is leftover under monthly-anchor billing; payment terms
 * are `grace_days`.
 */
export interface EffectiveCreditProfile {
  limit_usd_micros: number | null;
  cycle_days: number;
  grace_days: number;
}

export interface SubmitCreditApplicationRequest {
  org_id: string;
  requested_limit_cny_cents: number;
  requested_cycle_days: number;
  /** `finance_attachments.object_key` of an already-uploaded contract. */
  contract_attachment_key: string;
  /**
   * Required justification for the long-term line (router#2077). Trimmed
   * length must be in
   * `[CREDIT_APPLICATION_REASON_MIN_LENGTH, CREDIT_APPLICATION_REASON_MAX_LENGTH]`.
   */
  reason: string;
  /** Session ops user; ops-api stamps this — never client-supplied. */
  requester_ops_user_id: string;
}

/**
 * Submit never activates the line (router#1999). `approved` remains on
 * the application row after a later, distinct decide — not on this
 * response.
 */
export type CreditApplicationOutcome = 'pending_approval';

export interface WithinDefaultsCheck {
  /** requested CNY converted at submit-time policy rate <= default_limit_usd_micros. */
  limit: boolean;
  /** requested_cycle_days <= default_grace_days (账期, router#1999/#2061). */
  cycle: boolean;
}

export interface SubmitCreditApplicationResponse {
  outcome: CreditApplicationOutcome;
  application: CreditApplication;
  /** The account's credit columns AFTER the submit (unchanged when pending). */
  profile: CreditProfile;
  within_defaults: WithinDefaultsCheck;
}

export interface GetCreditApplicationsResponse {
  account_id: string;
  org_id: string;
  profile: CreditProfile;
  effective: EffectiveCreditProfile;
  defaults: CreditSettings;
  /** Newest first. */
  applications: CreditApplication[];
}
